import React, { useState } from 'react'

import ResultsSection from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/nonFcaServices/ResultsSection'
import ServicesSearchForm from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/nonFcaServices/ServicesSearchForm'
import SuggestNewServiceArea from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/nonFcaServices/SuggestNewServiceArea'
import LoadingCard from '@/components/misc/LoadingCard'
import SkeletonBox from '@/components/skeleton/SkeletonBox'
import useAppSelector from '@/hooks/useAppSelector'
import { useCurrentCompanyServicesQuery } from '@/hooks/useCompanyServicesQuery'
import { useCompanyType } from '@/hooks/useCompanyType'
import {
  selectServiceAreas,
  selectServiceAreasByCompanyTypeCode,
} from '@/redux/slices/commonData'
import {
  AvailableServiceAreaCollectionItem,
  CompanyItem,
} from '@/types/responses/companies'

interface Props {
  company: CompanyItem
  onUpdate?: () => void
}

const CompanyNonFcaServices: React.FC<Props> = ({ company, onUpdate }) => {
  const companyType = useCompanyType()
  const companyTypeServiceAreas = useAppSelector(
    selectServiceAreasByCompanyTypeCode(companyType.code),
  )
  const serviceAreas = useAppSelector(selectServiceAreas(false))
  const [searchTerm, setSearchTerm] = useState<string>('')
  const companyServicesQuery = useCurrentCompanyServicesQuery()
  const companyServicesData = companyServicesQuery.data

  if (companyServicesQuery.isLoading || !companyServicesData) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingCard />
      </div>
    )
  }

  const companyServices = companyServicesData['hydra:member']

  const companyTypeServiceAreaIds = companyTypeServiceAreas.map(
    (serviceArea) => serviceArea.id,
  )

  const otherServiceAreas = serviceAreas.filter(
    (serviceArea: AvailableServiceAreaCollectionItem) =>
      !companyTypeServiceAreaIds.includes(serviceArea.id),
  )

  function serviceAreaIsSearchMatch(
    serviceArea: AvailableServiceAreaCollectionItem,
  ): boolean {
    for (const searchWord of searchTerm.split(' ')) {
      if (!serviceArea.name.toLowerCase().includes(searchWord.toLowerCase())) {
        return false
      }
    }

    return true
  }

  const filteredCompanyTypeServiceAreas = companyTypeServiceAreas.filter(
    serviceAreaIsSearchMatch,
  )

  const filteredOtherServiceAreas = otherServiceAreas.filter(
    serviceAreaIsSearchMatch,
  )

  const totalResultsLength =
    filteredCompanyTypeServiceAreas.length + filteredOtherServiceAreas.length

  return (
    <section
      className="pop-in flex h-full flex-col items-center justify-center gap-4"
    >
      <ServicesSearchForm
        searchTerm={searchTerm}
        onChange={(newSearchTerm) => setSearchTerm(newSearchTerm)}
        selectedServices={companyServices}
        otherServicesString={company.otherServices ?? ''}
      />

      {companyServicesQuery.isPending ? (
        <SkeletonBox style={{ height: '200px' }} />
      ) : (
        <>
          <ResultsSection
            headingText="Suggested services for your firm"
            results={filteredCompanyTypeServiceAreas}
            searchTerm={searchTerm}
            onUpdate={onUpdate}
          />

          {searchTerm !== '' && (
            <ResultsSection
              headingText="Other services"
              results={filteredOtherServiceAreas}
              searchTerm={searchTerm}
              onUpdate={onUpdate}
            />
          )}

          {searchTerm !== '' && totalResultsLength === 0 && (
            <SuggestNewServiceArea
              inputText={searchTerm}
              currentOtherServicesString={company.otherServices ?? ''}
              onSave={() => setSearchTerm('')}
            />
          )}
        </>
      )}
    </section>
  )
}

export default CompanyNonFcaServices
